import React, { memo, useMemo } from 'react';
import Popover from '../profile/popover';
import { Icon } from '@common/components/icon';
import { combineClassNames } from '@utils/combineClassNames';
import { Popup } from 'semantic-ui-react';

import type { UserStatus } from '../../types/objects';
import { Emoji } from 'emoji-mart';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { formatUserStatus } from '@modules/core/selectors/logged-user';

type UserStatusLabelProps = {
  status: UserStatus
};

const formatExpiresAt = (expiresAtString: string, language: string, t: any) => {
  const expiresAt = moment(expiresAtString);
  if (expiresAt.isSame(new Date(), 'day')) {
    return expiresAt.format('HH:mm');
  }
  return (
    expiresAt.locale(language).format('LL') +
    ' ' + t('dates:at') + ' ' +
    expiresAt.format('HH:mm')
  );
};

export const UserStatusLabel = memo(({ status }: UserStatusLabelProps) => {
  const { t, i18n: { language } } = useTranslation();
  return (
    <span>
      {status.text}
      {
        status.expires_at &&
        <>
          {' '}{t('core:user_status_until')}{' '}
          {formatExpiresAt(status.expires_at, language, t)}
        </>
      }
    </span>
  );
});

type UserStatusBadgeProps = {
  formattedStatus: any;
  profileImageSize: number;
};

export const UserStatusBadge = memo(({
  formattedStatus,
  profileImageSize
}: UserStatusBadgeProps) => {
  // A status that never went through `formatUserStatus` has no resolved emoji
  // object. Render nothing rather than letting it take down the whole page.
  const emoji = formattedStatus?.emoji;
  if (!emoji || typeof emoji !== 'object') return null;

  return (
    <Popup
      position="left center"
      basic
      size="small"
      offset={[0, -5]}
      trigger={
        <div
          className="statusBadge"
          onClick={(event) => event.preventDefault()}
          style={{
            padding: Math.floor(profileImageSize / 12),
            bottom: Math.floor(profileImageSize / 20) * -1,
            left: Math.floor(profileImageSize / 10) * -1
          }}
        >
          <Emoji
            emoji={emoji.colons}
            size={Math.floor(profileImageSize / 3)}
          />
        </div>
      }
      content={<UserStatusLabel status={formattedStatus} />}
    />
  );
});

type ProfileImageUser = {
  id?: string;
  full_name?: string;
  profile_img?: string | null;
  last_active?: string | null;
  // User records use UserStatus; other user-like rows (e.g. evaluations) reuse `status`.
  status?: UserStatus | string | null;
};

type Props = {
  user: ProfileImageUser;
  size?: number;
  className?: string;
  enablePopover?: boolean;
  onEdit?: (user: ProfileImageUser, e: React.MouseEvent) => void;
  onClick?: React.MouseEventHandler;
  status?: UserStatus | null;
  formattedStatus?: UserStatus | null;
};

const defaultUser: ProfileImageUser = {
  id: '',
  full_name: '"Verwijderde gebruiker"',
  profile_img: '/static/images/user-placeholder.png',
  last_active: null,
};

export const ProfileImage = ({
  user = defaultUser,
  size = 40,
  className,
  enablePopover,
  onEdit,
  onClick,
  status,
  formattedStatus = null,
}: Props) => {
  const fullClassName = combineClassNames('ProfileImage Image', 'fs-exclude', className, {
    'ProfileImage--edit': !!onEdit,
  });

  const handleClick = (e: any) => {
    onEdit?.(user, e);
    onClick?.(e);
  };

  // Falls back to the status the API now returns on the user itself, so every
  // place a user is rendered shows their status without threading a prop
  // through each call site (ONE-4576). Ignore non-object `status` fields
  // (e.g. evaluation workflow enums on reviewee/reviewer rows).
  const fallbackStatus = user?.status && typeof user.status === 'object' ? user.status : null;
  const profileStatus = status !== undefined ? status : fallbackStatus;
  const resolvedStatus = useMemo(() => formatUserStatus(profileStatus), [profileStatus]);
  const userStatus = formattedStatus || resolvedStatus;

  const image = (
    <div
      className={fullClassName}
      style={{ width: size, height: size }}
      onClick={handleClick}
      role="button"
    >
      <img
        style={{
          width: size,
          height: size,
        }}
        src={user.profile_img || '/static/images/user-placeholder.png'}
        alt={user.full_name}
      />
      {onEdit && (
        <div className="ProfileImage__Edit">
          <Icon type="edit__filled" />
        </div>
      )}
      {
        userStatus ?
          <UserStatusBadge
            formattedStatus={userStatus}
            profileImageSize={size}
          /> :
          null
      }
    </div>
  );

  // @ts-expect-error
  return enablePopover ? <Popover user={user}>{image}</Popover> : image;
};

export default ProfileImage;
